import { useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  ClipboardList,
  Trophy,
  Users,
  Library,
  Building,
  FileText,
  BarChart3,
  ShieldCheck,
  UserRoundCheck
} from "lucide-react";
import { navigationItems } from "../../data/navigation";
import type { NavigationItem } from "../../types/navigation";
import { useAuthSession } from "../../contexts/AuthSessionContext";
import {
  canSeeLibraryNavigation,
  canShowContractsModule,
  canShowPrizeRulesModule,
  canShowRfpModule,
} from "../../utils/sessionModuleAccess";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  dashboard: LayoutDashboard,
  "file-stack": FileText,
  "clipboard-list": ClipboardList,
  trophy: Trophy,
  users: Users,
  grid: Library,
  building: Building,
  analytics: BarChart3,
  "shield-check": ShieldCheck,
  "representative-details": UserRoundCheck,
};

const NavIcon = ({ name }: { readonly name: string }) => {
  const Icon = iconMap[name];
  if (!Icon) return null;
  return <Icon className="w-5 h-5 text-white" />;
};

const isItemActive = (item: NavigationItem, pathname: string): boolean => {
  if (item.exact) return pathname === item.path;
  return pathname.startsWith(item.path);
};

function isNavigationVisible(
  item: NavigationItem,
  ctx: {
    readonly effectiveAccess: ReturnType<typeof useAuthSession>["effectiveAccess"];
    readonly adminRoles: readonly string[];
    readonly isShellAuthenticated: boolean;
  },
): boolean {
  if (!ctx.isShellAuthenticated) {
    return false;
  }
  if (item.alwaysForAuthenticated) {
    return true;
  }
  if (item.requireSuperAdmin) {
    return ctx.adminRoles.includes("SUPER_ADMIN");
  }
  if (item.requireLibrarySurface) {
    return canSeeLibraryNavigation(ctx.adminRoles);
  }
  if (item.requiredCapability === "contracts") {
    return canShowContractsModule(ctx.effectiveAccess, ctx.adminRoles);
  }
  if (item.requiredCapability === "rfp") {
    return canShowRfpModule(ctx.effectiveAccess, ctx.adminRoles);
  }
  if (item.requiredCapability === "prize_rules") {
    return canShowPrizeRulesModule(ctx.effectiveAccess, ctx.adminRoles);
  }
  return true;
}

export const SideMenu = () => {
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const { effectiveAccess, adminRoles, isShellAuthenticated } = useAuthSession();

  const visibleItems = useMemo(
    () =>
      navigationItems.filter((item) =>
        isNavigationVisible(item, { effectiveAccess, adminRoles, isShellAuthenticated }),
      ),
    [effectiveAccess, adminRoles, isShellAuthenticated],
  );

  return (
    <nav
      className="h-screen w-16 flex flex-col py-4"
      style={{ backgroundColor: "#009CE5" }}
    >
      {visibleItems.map((item) => {
        const active = isItemActive(item, location.pathname);
        const isDisabled = item.isDisabled === true;
        const isHovered = hoveredItem === item.id;

        return (
          <div
            key={item.id}
            className="relative group"
            onMouseEnter={() => !isDisabled && setHoveredItem(item.id)}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <NavLink
              to={isDisabled ? "#" : item.path}
              onClick={(e) => isDisabled && e.preventDefault()}
              className={`w-full h-12 flex items-center justify-center transition-colors ${
                active ? "bg-white/10" : !isDisabled ? "hover:bg-white/10" : ""
              } ${isDisabled ? "cursor-default opacity-50" : "cursor-pointer"}`}
              aria-label={item.label}
            >
              <NavIcon name={item.icon ?? ""} />
            </NavLink>

            {isHovered && !isDisabled && (
              <div
                className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-1.5 rounded whitespace-nowrap pointer-events-none z-50"
                style={{
                  backgroundColor: "#009CE5",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                }}
              >
                <span className="text-sm text-white font-normal">
                  {item.label}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};
